import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import { LtvCheckpointDto, LtvMetricsDto, LtvReportDto, MarketingAcquisitionService } from '../../../../services/marketing-acquisition.service';
import type { AcquisitionDateRange } from '../acquisition.component';

/**
 * 12-month LTV for customers whose account was created in the range, by calendar-quarter cohort and
 * by acquisition channel. Shown under the retention tab; the clock starts at each first pickup.
 */
@Component({
  selector: 'app-acquisition-ltv',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ltv.component.html'
})
export class LtvComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  readonly checkpointDays = [30, 90, 180, 365];

  loading = false;
  errorMessage = '';
  report: LtvReportDto | null = null;

  private readonly load$ = new Subject<AcquisitionDateRange>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(range => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getLtv(range.from, range.to).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => {
        this.report = report;
        this.loading = false;
      });

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.load$.next(this.range));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.load$.next(this.range);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkpoint(metrics: LtvMetricsDto, days: number): LtvCheckpointDto | undefined {
    return metrics.checkpoints.find(c => c.days === days);
  }

  /** "Jan–Mar 2026" for "2026-Q1". */
  quarterLabel(quarter: string): string {
    const [year, q] = quarter.split('-Q');
    const months = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Oct–Dec'];
    return `${months[Number(q) - 1] ?? quarter} ${year}`;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load LTV. Please try again.';
    }
  }
}
